import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { DatePicker, Spin, Card, Statistic, Table, Row, Col, Badge, Tag } from "antd";
import {
    Box,
    Typography,
    Paper,
    Container,
    Grid,
    Avatar,
    Chip,
    Divider,
    Stack
} from "@mui/material";
import { motion } from "framer-motion";
import Chart from "react-apexcharts";
import dayjs from "dayjs";
import { getData } from "store/shop/shop";
import {
    // TrendingUpOutlined,
    CalendarOutlined,
    DollarOutlined,
    ShopOutlined,
    UserOutlined
} from "@ant-design/icons";

const columns = [
    // {
    //     title: "Bill ID",
    //     dataIndex: "bill_id",
    //     key: "bill_id",
    //     render: (val) => <Tag color="blue">#{val}</Tag>
    // },
    // {
    //     title: "Customer",
    //     dataIndex: "customer_id",
    //     key: "customer_id",
    //     render: (val) => (
    //         <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    //             <Avatar size="small" icon={<UserOutlined />} />
    //             <span>ID: {val}</span>
    //         </div>
    //     )
    // },
    {
        title: "Order ID",
        dataIndex: "order_id",
        key: "order_id",
        render: (val) => <Tag color="green">#{val}</Tag>
    },
    {
        title: "Total Amount",
        dataIndex: "total_amount",
        key: "total_amount",
        render: (val) => <strong>${val}</strong>,
        sorter: (a, b) => a.total_amount - b.total_amount,
    },
    {
        title: "Discount",
        dataIndex: "discount",
        key: "discount",
        render: (val) => <span style={{ color: '#52c41a' }}>-${val}</span>,
    },
    {
        title: "Bill Amount",
        dataIndex: "bill_amount",
        key: "bill_amount",
        render: (val) => <strong style={{ color: '#1890ff' }}>${val}</strong>,
        sorter: (a, b) => a.bill_amount - b.bill_amount,
    },
    {
        title: "Date",
        dataIndex: "bill_date",
        key: "bill_date",
        render: (val) => dayjs(val).format("MMM DD, YYYY"),
        sorter: (a, b) => dayjs(a.bill_date) - dayjs(b.bill_date),
    },
    {
        title: "Status",
        dataIndex: "bill_status",
        key: "bill_status",
        render: (val) => (
            <Badge
                status={val === 1 ? "success" : "error"}
                text={val === 1 ? "Paid" : "Unpaid"}
            />
        ),
    },
];

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

function ViewTenantReport() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [report, setReport] = useState(null);

    const now = dayjs();
    const month = searchParams.get("month") || now.month() + 1;
    const year = searchParams.get("year") || now.year();

    useEffect(() => {
        setLoading(true);
        getData(
            `https://admin.multitenant.pos.alisonstech-dev.com/Api/api/tenants/${id}/sales-report?month=${month}&year=${year}`
        )
            .then((data) => {
                console.log("Fetched report data:", data);
                setReport(data?.data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [id, month, year]);

    const handleDateChange = (date) => {
        if (!date) return;
        setSearchParams({
            month: date.month() + 1,
            year: date.year(),
        });
    };

    const chartOptions = {
        chart: {
            type: "bar",
            height: 300,
            toolbar: { show: false },
            background: 'transparent'
        },
        plotOptions: {
            bar: {
                borderRadius: 8,
                columnWidth: '60%',
            }
        },
        xaxis: {
            categories: report?.sales_report?.map((r) => dayjs(r.bill_date).format("MMM DD")) || [],
            labels: {
                style: {
                    colors: '#666',
                    fontSize: '12px'
                }
            }
        },
        yaxis: {
            labels: {
                formatter: (val) => `$${val}`,
                style: {
                    colors: '#666',
                    fontSize: '12px'
                }
            }
        },
        colors: ['#1890ff'],
        dataLabels: { enabled: false },
        grid: {
            borderColor: '#f0f0f0',
            strokeDashArray: 5
        },
        title: {
            text: "Daily Bill Amounts",
            align: "left",
            style: {
                fontSize: '16px',
                fontWeight: '600',
                color: '#333'
            }
        },
    };

    const chartSeries = [
        {
            name: "Bill Amount",
            data: report?.sales_report?.map((r) => r.bill_amount) || [],
        },
    ];

    const getStatusColor = (status) => {
        return status === 'active' ? 'success' : 'default';
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '60vh'
                }}
            >
                <Spin size="large" tip="Loading report..." />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Header */}
                <motion.div variants={itemVariants}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            <ShopOutlined style={{ marginRight: 12, color: '#1890ff' }} />
                            Sales Report
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                            View detailed sales analytics for {dayjs(`${year}-${month}`).format("MMMM YYYY")}
                        </Typography>
                    </Box>
                </motion.div>

                {/* Date Filter */}
                <motion.div variants={itemVariants}>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            mb: 3,
                            borderColor: '#e0e0e0',
                            background: '#fff'
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <CalendarOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                            <Typography variant="subtitle1" fontWeight="500">
                                Filter by Month
                            </Typography>
                            <DatePicker
                                picker="month"
                                value={dayjs(`${year}-${month}`)}
                                onChange={handleDateChange}
                                format="YYYY-MM"
                                allowClear={false}
                                size="middle"
                            />
                        </Stack>
                    </Paper>
                </motion.div>

                {/* Stats Cards */}
                <motion.div variants={itemVariants}>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={4}>
                            <Card bordered style={{ height: '100%', borderColor: '#e15b2c' }}>
                                <Statistic
                                    title={`Amount to be Paid - ${report?.tenant_info?.plan?.replaceAll('_', ' ') || ''}`}
                                    value={report?.tenant_info?.plan?.includes('monthly')?report?.tenant_info?.plan?.includes('percent')
                                        ? `${Math.round(+report?.monthly_total * (+report?.tenant_info?.price) / 100)}`
                                        : report?.tenant_info?.price || 0:report?.tenant_info?.plan?.includes('one')? report?.tenant_info?.price || 0:report?.tenant_info?.plan?.includes('percent')
                                        ? `${Math.round(+report?.yearly_total * (+report?.tenant_info?.price) / 100)}`
                                        : report?.tenant_info?.price || 0
                                    }
                                    prefix={<ShopOutlined style={{ color: '#e15b2c' }} />}
                                    precision={2}
                                    valueStyle={{ color: '#e15b2c', fontSize: '24px' }}
                                />
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card bordered style={{ height: '100%', borderColor: '#e0e0e0' }}>
                                <Statistic
                                    title="Yearly Total"
                                    value={report?.yearly_total || 0}
                                    precision={2}
                                    prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
                                    valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                                />
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card bordered style={{ height: '100%', borderColor: '#e0e0e0' }}>
                                <Statistic
                                    title="Monthly Total"
                                    value={report?.monthly_total || 0}
                                    precision={2}
                                    prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
                                    valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                                />
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card bordered style={{ height: '100%', borderColor: '#e0e0e0' }}>
                                <Statistic
                                    title="All Time Sales"
                                    value={report?.all_time_sales || 0}
                                    precision={2}
                                    valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                                />
                            </Card>
                        </Grid>
                        
                    </Grid>
                </motion.div>

                {/* Tenant Info - Separate Row */}
                <motion.div variants={itemVariants}>
                    <Paper
                        variant="outlined"
                        sx={{ p: 3, mb: 4, borderColor: '#e0e0e0', background: '#fff' }}
                    >
                        <Typography variant="h6" gutterBottom color="primary">
                            <UserOutlined style={{ marginRight: 8 }} />
                            Tenant Info
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="textSecondary">Name</Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {report?.tenant_info?.name || "N/A"}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="textSecondary">Domain</Typography>
                                <Chip
                                    label={report?.tenant_info?.domain}
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="textSecondary">Status</Typography>
                                <Chip
                                    label={report?.tenant_info?.status}
                                    // /color={getStatusColor(report?.tenant_info?.status)}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="textSecondary">Plan</Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {report?.tenant_info?.plan}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="textSecondary">Price</Typography>
                                <Typography variant="body1" fontWeight="bold" color="success.main">
                                    {report?.tenant_info?.plan?.includes('percent')
                                        ? `${Math.round(+report?.tenant_info?.price)}%`
                                        : `$${report?.tenant_info?.price}`
                                    }
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="textSecondary">Created</Typography>
                                <Typography variant="body1">
                                    {dayjs(report?.tenant_info?.created_at).format("MMM DD, YYYY")}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                </motion.div>


                {/* Chart */}
                <motion.div variants={itemVariants}>
                    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                        <Chart
                            options={chartOptions}
                            series={chartSeries}
                            type="bar"
                            height={300}
                        />
                    </Paper>
                </motion.div>

                {/* Table */}
                <motion.div variants={itemVariants}>
                    <Paper elevation={3} sx={{ p: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: '#1890ff' }}>
                            Sales Details
                        </Typography>
                        <Table
                            columns={columns}
                            dataSource={report?.sales_report || []}
                            rowKey="bill_id"
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} items`
                            }}
                            scroll={{ x: 800 }}
                            size="middle"
                        />
                    </Paper>
                </motion.div>
            </motion.div>
        </Container>
    );
}

export default ViewTenantReport;